import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { InputStatus } from '../../../models/error.model';
import { IUserService } from '../../user/user.service';
import { IPlanInfo, IUserStore, Plan } from '../../user/user.store';
import { ChangeUserPlanModal } from '../components/ChangeUserPlanModal';

interface IProps {
  showModal: boolean;
  userId: string;
  onClose: () => void;
}

export const ChangeUserPlanContainer = observer((props: IProps): ReactElement => {
  const userStore = useInjection(IUserStore.$);
  const userService = useInjection(IUserService.$);

  const [availableSpaceStatus, setAvailableSpaceStatus] = useState(InputStatus.empty());
  const [workspacesStatus, setWorkspacesStatus] = useState(InputStatus.empty());
  const [workspaceUsersStatus, setWorkspaceUsersStatus] = useState(InputStatus.empty());
  const [planInfo, setPlanInfo] = useState<IPlanInfo>(IPlanInfo.Empty);

  useEffect(() => {
    const planInfo = userStore.adminUserInfo.find((p) => p.id === props.userId) ?? IPlanInfo.Empty;

    const defaultPlan = getDefaultPlan(planInfo.plan);
    planInfo.availableSpace = planInfo.availableSpace || defaultPlan.availableSpace;
    planInfo.workspaceUsers = planInfo.workspaceUsers || defaultPlan.workspaceUsers;
    planInfo.workspaces = planInfo.workspaces || defaultPlan.workspaces;

    const validate = planInfo && planInfo.customLimits;

    setPlanInfo(planInfo);
    setAvailableSpaceStatus(validate ? InputStatus.buildFrom(getErrorCodes(planInfo.availableSpace)) : InputStatus.empty());
    setWorkspaceUsersStatus(validate ? InputStatus.buildFrom(getErrorCodes(planInfo.workspaceUsers)) : InputStatus.empty());
    setWorkspacesStatus(validate ? InputStatus.buildFrom(getErrorCodes(planInfo.workspaces)) : InputStatus.empty());
  }, [props.showModal]);

  const getErrorCodes = (value: number | undefined): TranslationKey[] =>
    value === undefined || value === null || isNaN(value) || value < -1 || value === 0 ? ['field_is_required'] : [];

  const getDefaultPlan = (plan?: Plan): IPlanInfo => {
    const defaultPlan: IPlanInfo | undefined = userStore.planDefaults.find((p) => p.plan === (plan || planInfo?.plan || Plan.Free));

    if (!defaultPlan) {
      throw 'Plan not found';
    }

    return defaultPlan;
  };

  const getStatusSetter = (name: string): Dispatch<SetStateAction<InputStatus>> => {
    switch (name) {
      case 'availableSpace':
        return setAvailableSpaceStatus;
      case 'workspaces':
        return setWorkspacesStatus;
      case 'workspaceUsers':
        return setWorkspaceUsersStatus;
      default:
        return () => {};
    }
  };

  const validateInput = (name: string): void => {
    const getValueByKey = <T,>(obj: T, key: keyof T): T[keyof T] => obj[key];

    if (planInfo) {
      const value = getValueByKey(planInfo, name as keyof IPlanInfo);

      if (typeof value === 'number') {
        const statusSetter = getStatusSetter(name);
        statusSetter(InputStatus.buildFrom(getErrorCodes(value)));
      }
    }
  };

  const handleLimitToggle = (name: string, unlimited: boolean): void => {
    setPlanInfo((prevPlanInfo) => {
      if (!prevPlanInfo) {
        throw 'plan not initialized';
      }
      return {
        ...prevPlanInfo,
        [name]: unlimited ? -1 : undefined,
      };
    });
    const statusSetter = getStatusSetter(name);
    statusSetter(InputStatus.buildFrom(getErrorCodes(unlimited ? -1 : undefined)));
  };

  const handleLimitChange = (name: string, value?: number): void => {
    setPlanInfo((prevPlanInfo) => {
      if (!prevPlanInfo) {
        throw 'plan not initialized';
      }
      return {
        ...prevPlanInfo,
        [name]: value,
      };
    });
  };

  const handleReset = (): void => {
    const planDefaults = getDefaultPlan();

    setPlanInfo((prevPlanInfo) => {
      if (!prevPlanInfo) {
        throw 'plan not initialized';
      }
      return {
        ...prevPlanInfo,
        availableSpace: planDefaults.availableSpace,
        workspaces: planDefaults.workspaces,
        workspaceUsers: planDefaults.workspaceUsers,
      };
    });
    setAvailableSpaceStatus(InputStatus.empty());
    setWorkspacesStatus(InputStatus.empty());
    setWorkspaceUsersStatus(InputStatus.empty());
  };

  const handleSavePlanChange = async (): Promise<void> => {
    if (isFormValid() && planInfo) {
      await userService.changeUserPlanAsync(props.userId, planInfo);
      props.onClose();
    } else {
      validateForm();
    }
  };

  const validateForm = (): void => {
    setAvailableSpaceStatus(InputStatus.buildFrom(getErrorCodes(planInfo?.availableSpace)));
    setWorkspacesStatus(InputStatus.buildFrom(getErrorCodes(planInfo?.workspaces)));
    setWorkspaceUsersStatus(InputStatus.buildFrom(getErrorCodes(planInfo?.workspaceUsers)));
  };

  const isFormValid = (): boolean => {
    return (
      !!planInfo &&
      (!planInfo.customLimits ||
        (getErrorCodes(planInfo.availableSpace).length === 0 &&
          getErrorCodes(planInfo.workspaces).length === 0 &&
          getErrorCodes(planInfo.workspaceUsers).length === 0 &&
          getErrorCodes(planInfo.workspaceUsers).length === 0))
    );
  };

  const handleCustomLimitsChange = (customLimits: boolean): void => {
    const planDefaults = getDefaultPlan(planInfo?.plan);

    setPlanInfo((prevPlanInfo) => {
      if (!prevPlanInfo) {
        throw 'plan not initialized';
      }
      return {
        ...prevPlanInfo,
        customLimits,
        availableSpace: planDefaults.availableSpace,
        workspaces: planDefaults.workspaces,
        workspaceUsers: planDefaults.workspaceUsers,
      };
    });

    if (!customLimits) {
      setWorkspacesStatus(InputStatus.empty());
      setAvailableSpaceStatus(InputStatus.empty());
      setWorkspaceUsersStatus(InputStatus.empty());
    }
  };

  const handlePlanChange = (plan: Plan): void => {
    const defaultPlan = getDefaultPlan(plan);
    setPlanInfo((prevPlanInfo) => {
      if (!prevPlanInfo) {
        throw 'plan not initialized';
      }
      return {
        ...prevPlanInfo,
        plan,
        availableSpace: !prevPlanInfo.customLimits ? defaultPlan.availableSpace : prevPlanInfo.availableSpace,
        workspaces: !prevPlanInfo.customLimits ? defaultPlan.workspaces : prevPlanInfo.workspaces,
        workspaceUsers: !prevPlanInfo.customLimits ? defaultPlan.workspaceUsers : prevPlanInfo.workspaceUsers,
      };
    });
  };

  return (
    <ChangeUserPlanModal
      changeUserPlanModalShown={props.showModal}
      planInfo={planInfo}
      planDefaults={userStore.planDefaults}
      availableSpaceStatus={availableSpaceStatus}
      workspacesStatus={workspacesStatus}
      workspaceUsersStatus={workspaceUsersStatus}
      onNumericInputBlur={validateInput}
      onSavePlanClicked={handleSavePlanChange}
      onPlanChange={handlePlanChange}
      onCustomLimitsChange={handleCustomLimitsChange}
      onLimitToggle={handleLimitToggle}
      onLimitChange={handleLimitChange}
      onReset={handleReset}
      onClose={props.onClose}
    />
  );
});
